import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntraAuthProvider, EntraLoginButton, useEntraAuth } from './components/EntraAuth';
import { Card } from './components/ui';
import './LoginEntra.css';

const LoginEntraContent = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user, loading, login } = useEntraAuth();

    // State for messages
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

    // Auto-login when coming from webapp
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const autoLogin = urlParams.get('autoLogin');

        if (autoLogin === 'true' && !isAuthenticated && !loading && !autoLoginAttempted) {
            console.log('🔄 Auto-login triggered from webapp...');
            setAutoLoginAttempted(true);
            login();
        }
    }, [isAuthenticated, loading, login, autoLoginAttempted]);

    // Check authentication status on component mount (no auto-redirect)
    useEffect(() => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');

        if (token && user) {
            try {
                const userData = JSON.parse(user);
                setSuccess(`Welcome back, ${userData.name || userData.email}!`);
            } catch (parseError) {
                console.error('Error parsing stored user data:', parseError);
                setError('Invalid stored authentication data. Please login again.');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('authType');
            }
        }
    }, []);

    // Redirect when authentication succeeds
    useEffect(() => {
        if (isAuthenticated && user && !loading) {
            setError('');
            setSuccess('Microsoft authentication successful! Redirecting...');
            setTimeout(() => {
                navigate('/dashboard');
            }, 1500);
        }
    }, [isAuthenticated, user, loading, navigate]);

    // Handle Entra ID login error
    const handleEntraLoginError = (error) => {
        console.error('Entra login failed:', error);
        const errorMessage = error?.message || error?.errorMessage || 'Authentication failed. Please try again.';
        setError(`Authentication failed: ${errorMessage}`);
        setSuccess('');
    };

    return (
        <div className="login-entra-container">
            <div className="login-entra-card">
                {/* Header */}
                <div className="login-entra-header">
                    <a className="home-link" title="Înapoi la pagina principală" href="https://evoomedical.ro">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                            <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></polyline>
                        </svg>
                    </a>
                    <h1 className="login-entra-title">Evoo Medical Compas</h1>
                </div>

                {/* Error Message */}
                {error && (
                    <Card className="error-message">
                        <div className="message-content">
                            <span className="message-icon">⚠️</span>
                            <span>{error}</span>
                        </div>
                    </Card>
                )}

                {/* Success Message */}
                {success && (
                    <Card className="success-message">
                        <div className="message-content">
                            <span className="message-icon">✅</span>
                            <span>{success}</span>
                        </div>
                    </Card>
                )}

                {/* Microsoft Login Section */}
                <div className="microsoft-login-section">
                    <h3 className="microsoft-login-title">Autentificare Evoo Medical</h3>

                    <p className="microsoft-login-description">
                        Folosiți contul dvs. Microsoft Entra ID pentru a accesa în siguranță platforma Evoo Medical.
                    </p>

                    <EntraLoginButton
                        onLoginError={handleEntraLoginError}
                    />
                </div>

                {/* Navigation Options */}
                <div className="navigation-section">
                    <div className="login-info">
                        <h4>Informații despre autentificare:</h4>
                        <ul>
                            <li><strong> Medici și angajați:</strong> Folosiți autentificarea Microsoft pentru acces securizat la sistemul medical</li>
                            <li><strong> Pacienți:</strong> Pentru programări online, contactați clinica pentru asistență</li>
                        </ul>
                        <p className="login-note">Dacă întâmpinați probleme cu autentificarea, <a href="mailto:webadmin@evoomedical.ro">contactați</a> administratorul sistemului.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const LoginEntra = () => {
    return (
        <EntraAuthProvider>
            <LoginEntraContent />
        </EntraAuthProvider>
    );
};

export default LoginEntra;
